import { AddOnProductType } from "@stores/addons/types";
import { ICreatePurchaseAddOn } from "@stores/purchases/types";
import dummy from "./dummy";
import dwh from "./dwh";
import dwhRent from "./dwh-rent";
import etlConnections from "./etl-connections";
import metabaseAi from "./metabase-ai";
import metabaseAiManaged from "./metabase-ai-managed";
import metabaseAiTiered from "./metabase-ai-tiered";
import productAnalytics from "./product-analytics";
import productAnalyticsFree from "./product-analytics-free";
import pythonExecution from "./python-execution";
import semanticSearch from "./semantic-search";
import transformsAdvanced from "./transforms-advanced";
import transformsAdvancedMetered from "./transforms-advanced-metered";
import transformsBasic from "./transforms-basic";
import transformsBasicMetered from "./transforms-basic-metered";
import {
  AddOnSlot,
  IPurchaseContext,
  IAddOnDescriptor,
  IAddOnContext,
  IAddOnRow,
  ICheckoutRow,
  IUpsertContext,
} from "./types";

const DESCRIPTORS = [
  metabaseAiManaged,
  metabaseAiTiered,
  metabaseAi,
  transformsBasicMetered,
  transformsAdvancedMetered,
  transformsBasic,
  transformsAdvanced,
  dwh,
  dwhRent,
  productAnalytics,
  productAnalyticsFree,
  etlConnections,
  semanticSearch,
  pythonExecution,
  dummy,
] as const satisfies readonly IAddOnDescriptor[];

export const ADD_ONS_DESCRIPTORS: readonly IAddOnDescriptor[] = DESCRIPTORS;

export type DescriptorOf<T extends AddOnProductType> = Extract<
  (typeof DESCRIPTORS)[number],
  { productType: T }
>;

const BY_PRODUCT_TYPE = new Map<string, IAddOnDescriptor>(
  ADD_ONS_DESCRIPTORS.map((descriptor) => [descriptor.productType, descriptor]),
);

export function getDescriptor(
  productType: AddOnProductType,
): IAddOnDescriptor | undefined;
export function getDescriptor(productType: string): undefined;
export function getDescriptor(productType: string) {
  return BY_PRODUCT_TYPE.get(productType);
}

export const buildAddOnContext = <D extends IAddOnDescriptor>(
  descriptor: D,
  ctx: IPurchaseContext,
): IAddOnContext<D> => {
  // built kind-agnostic; the descriptor's kind is what narrows it for callers
  const addOnCtx: IAddOnContext = {
    ...ctx,
    productType: descriptor.productType,
    subscribed: ctx.subscribedByType[descriptor.productType],
    addOn: ctx.addOnsByType[descriptor.productType],
    getEnableAddOnPayload: (units?: number) => ({
      new_plan_alias: ctx.purchase.product.plan.alias,
      upsert_add_ons: ctx.getUpsertAddOnsFor(descriptor.productType, units),
    }),
    getDisableAddOnPayload: () => ({
      new_plan_alias: ctx.purchase.product.plan.alias,
      remove_add_ons: ctx.getRemoveAddOnsFor(descriptor.productType),
    }),
    getUsage: () => descriptor.getUsage?.(addOnCtx),
    getPricePerUnit: () => descriptor.getPricePerUnit?.(addOnCtx),
    getIncludedUnits: () => descriptor.getIncludedUnits?.(addOnCtx),
    isVisible: () => descriptor.isVisible?.(addOnCtx) ?? false,
    title: descriptor.title,
    getEstimateRowItem: (units: number) =>
      descriptor.getEstimateRowItem?.(addOnCtx, units),
    // the slot was declared against this descriptor's kind, which is the
    // kind this context was built from
    ManageCard: descriptor.ManageCard as AddOnSlot | undefined,
  };

  return addOnCtx as IAddOnContext<D>;
};

/**
 * Any subscribed metered add-on, whose bill grows with usage — so the invoice
 * total for the next charge cannot be trusted yet (GRO-570).
 *
 * The free Track grant is metered at a zero rate and so never actually bills,
 * but it is not worth a second billing flag and a per-descriptor exception to
 * keep quoting a total for it: the grant is not going to be a widespread state,
 * and hiding a trustworthy total is the harmless direction to be wrong in.
 */
export function billsOnUsage(ctx: IPurchaseContext): boolean {
  return ADD_ONS_DESCRIPTORS.some(
    (descriptor) =>
      !!ctx.subscribedByType[descriptor.productType] &&
      descriptor.kind === "metered",
  );
}

/** every upsert add-on whose descriptor quotes it, in payload order */
export function checkoutLineItems(
  ctx: IUpsertContext,
  upsertAddOns: ICreatePurchaseAddOn[],
): ICheckoutRow[] {
  return upsertAddOns.flatMap(({ product_type, prepaid_units }) => {
    const descriptor = getDescriptor(product_type);
    const addOn = ctx.addOnsByType[product_type];

    if (!descriptor?.getCheckoutRowItem || !addOn) {
      return [];
    }

    const { productType, title } = descriptor;

    return [
      {
        productType,
        title,
        ...descriptor.getCheckoutRowItem(
          { ...ctx, productType, addOn, title },
          prepaid_units,
        ),
      },
    ];
  });
}

/**
 * Every row the billing summary owes this subscription, in subscription order.
 * Not filtered by `isVisible`: that answers "show a manage card", while a row
 * is owed for anything subscribed whose descriptor bills it.
 */
export function billingRowItems(ctx: IPurchaseContext): IAddOnRow[] {
  return Object.values(ctx.subscribedByType).flatMap((subscribed) => {
    const descriptor = getDescriptor(subscribed.product_type);

    return descriptor?.getBillingRowItem
      ? [
          {
            productType: descriptor.productType,
            title: descriptor.title,
            ...descriptor.getBillingRowItem(buildAddOnContext(descriptor, ctx)),
          },
        ]
      : [];
  });
}

export function visibleAddOns(ctx: IPurchaseContext): IAddOnContext[] {
  return ADD_ONS_DESCRIPTORS.map((descriptor) =>
    buildAddOnContext(descriptor, ctx),
  ).filter((addOn) => addOn.isVisible());
}

/**
 * Every invoice row, in registry order. Deliberately not filtered by
 * `isVisible` — that predicate answers "should this add-on show a manage
 * card", which forks on deployment and offer flags, while a billing row is
 * owed for anything the subscription carries. Each row gates on its own
 * context.
 */
export function purchaseLineItems(ctx: IPurchaseContext): IAddOnRow[] {
  return ADD_ONS_DESCRIPTORS.flatMap((descriptor) => {
    const item = descriptor.getPurchaseLineItem?.(
      buildAddOnContext(descriptor, ctx),
    );

    return item
      ? [
          {
            productType: descriptor.productType,
            title: descriptor.title,
            ...item,
          },
        ]
      : [];
  });
}
